import { Controller } from "@hotwired/stimulus"
import { triggerHaptic } from "../src/trigger_native_haptic"

// Drives a horizontal snap carousel where neighbours peek in at the edges:
// reports "N of M" as the user swipes, and scales each slide by its distance
// from the track's centre so the current card eases down as the next one
// grows in (Flo-style).
export default class extends Controller {
  static targets = ["track", "slide", "current"]

  static values = {
    minScale: { type: Number, default: 0.90 },
    hapticLevel: { type: String, default: "light" },
    hapticOnChange: { type: Boolean, default: false },
    currentIndex: { type: Number, default: 0 }
  }

  connect() {
    this.persistCurrentIndex = this.persistCurrentIndex.bind(this)
    document.addEventListener("turbo:before-cache", this.persistCurrentIndex)

    this.restoreCurrentSlide()
    // Sync to the actual position before the first update so restoring a
    // cached page never fires a haptic.
    this.currentIndexValue = this.closestSlideIndex()
    this.update()
  }

  update() {
    if (this.frame) return

    this.frame = requestAnimationFrame(() => {
      this.frame = null
      const slideIndex = this.closestSlideIndex()
      const changed = slideIndex !== this.currentIndexValue

      this.scaleSlides()
      this.currentTarget.textContent = slideIndex + 1
      this.currentIndexValue = slideIndex
      if (changed && this.hapticOnChangeValue) triggerHaptic(this.hapticLevelValue)
    })
  }

  disconnect() {
    if (this.frame) cancelAnimationFrame(this.frame)

    this.persistCurrentIndex()
    document.removeEventListener("turbo:before-cache", this.persistCurrentIndex)

    this.frame = null
  }

  scaleSlides() {
    const trackCenter = this.trackCenter()

    this.slideTargets.forEach((slide) => {
      const rect = slide.getBoundingClientRect()
      const slideCenter = rect.left + rect.width / 2

      // 0 when centred, 1 once the slide is a full width off-centre.
      const offset = Math.min(Math.abs(slideCenter - trackCenter) / rect.width, 1)
      const scale = 1 - (1 - this.minScaleValue) * offset

      slide.style.transform = `scale(${scale})`
    })
  }

  closestSlideIndex() {
    const trackCenter = this.trackCenter()

    let closest = 0
    let closestDistance = Infinity

    this.slideTargets.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect()
      const slideCenter = rect.left + rect.width / 2
      const distance = Math.abs(slideCenter - trackCenter)

      if (distance < closestDistance) {
        closestDistance = distance
        closest = index
      }
    })

    return closest
  }

  trackCenter() {
    const rect = this.trackTarget.getBoundingClientRect()
    return rect.left + rect.width / 2
  }

  restoreCurrentSlide() {
    if (!this.slideTargets[this.currentIndexValue]) return

    this.scrollToSlide(this.currentIndexValue)
  }

  persistCurrentIndex() {
    this.currentIndexValue = this.closestSlideIndex()
  }

  scrollToSlide(slideIndex) {
    const slide = this.slideTargets[slideIndex]
    const slideCenter = slide.offsetLeft + slide.offsetWidth / 2
    const trackCenter = this.trackTarget.clientWidth / 2

    this.trackTarget.scrollLeft = Math.max(0, slideCenter - trackCenter)
  }
}
